import { Injectable } from '@angular/core';
import { EsriIdentify, EsriIdentifyParameters } from '../../config/esri.module';
import { ArbeitsbereichService } from './arbeitsbereich.service';
import { WidgetNotifyService } from './widget-notify.service';
import { CONSTANTS_VALUE } from '../../config/constants.module';
import { MainMapService } from './main-map.service';
import { PopupService } from '../../component/tools/popup/service/popup.service';
import { Layer } from '../model/layer.model';
import {
  FeatureIdentifyResult,
  IdentifyLayerConfig,
  IdentifyResultInfo,
  IdentifyServices,
} from '../../config/model/identify-config.model';
import { forkJoin, from, Observable } from 'rxjs';
import { ConfigService } from '../../config/config.service';
import { LayerContainerService } from './layer-container.service';
import { DetailViewFeatureParams } from '../../component/detail-view/detail-view.model';
import { SelectionService } from './selection.service';
import { tap, map } from 'rxjs/operators';

/**
 * @class IdentifyService
 * @version 0.0.1
 * @description The IdentifyService provides to identify tasks
 */
@Injectable({
  providedIn: 'root',
})
export class IdentifyService {
  private isActive = true;

  // new
  private identifyServices: IdentifyServices[];

  constructor(
    private mainMapService: MainMapService,
    private arbeitsbereichService: ArbeitsbereichService,
    private popupService: PopupService,
    private widgetNotifyService: WidgetNotifyService,
    private configService: ConfigService,
    private layerContainerService: LayerContainerService,
    private selectionService: SelectionService
  ) {
    this.identifyServices = [];
    // listen on map click
    this.mainMapService.mapView.on('click', event => {
      if (this.isActive && this.mainMapService.mapInteractionActivated) {
        this.onMapClick(event);
      }
    });
  }

  public buildIdentifyTask(lyrs: Layer[]) {
    if (lyrs?.length > 0) {
      this.identifyServices.push({ url: lyrs[0].layerUrl, fkLayer: lyrs[0].fkLayer });
    }
  }

  /**
   * @function buildIdentifyParameters
   * @description Erstellt die richtigen Parameter für das ItentifyTask
   */
  private buildIdentifyParameters(
    identifyParamsConfig: IdentifyLayerConfig,
    evt: __esri.ViewClickEvent
  ): __esri.IdentifyParameters {
    const params = new EsriIdentifyParameters();

    if (identifyParamsConfig) {
      params.tolerance = identifyParamsConfig.tolerance;
      params.layerIds = this.getIdentifyLayerIds(identifyParamsConfig);
    } else {
      params.layerIds = [];
    }

    params.layerOption = 'visible';
    params.returnGeometry = true;
    params.geometry = evt.mapPoint;
    params.mapExtent = this.mainMapService.mapView.extent;
    params.width = this.mainMapService.mapView.width;
    params.height = this.mainMapService.mapView.height;

    return params;
  }

  // ---- PRIVATE CORE METHODS ---- //

  private onMapClick(event: __esri.ViewClickEvent) {
    this.widgetNotifyService.onMapClickedSubject.next(true);
    if (event.button === 2) {
      this.popupService.buildCoordinatesPopup(event);
    } else {
      this.executeIdentifyTask(event);
    }
  }

  private executeIdentifyTask(evt: __esri.ViewClickEvent) {
    if (this.identifyServices.length === 0) return;

    this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.wait);

    // Filter and create an observable for each identify request
    const identifyObservables: Observable<any>[] = this.identifyServices
      .filter(identifyService => this.layerContainerService.isIdentifyLayerVisible(identifyService.fkLayer))
      .map(identifyService => this.createIdentifyObservable(identifyService, evt));

    if (identifyObservables.length === 0) {
      this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
      return;
    }

    // wait for all identify operations to complete
    forkJoin(identifyObservables)
      .pipe(
        tap({
          complete: () => this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default),
          error: () => this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default),
        })
      )
      .subscribe({
        next: (responses: FeatureIdentifyResult[]) => {
          this.processIdentifyResponses(responses, evt);
        },
        error: error => {
          console.error('Identify operation failed:', error);
        },
      });
  }

  private createIdentifyObservable(identifyService: any, evt: __esri.ViewClickEvent): Observable<any> {
    const identifyLayerConfig = this.getIdentifyLayerConfig(identifyService.fkLayer);
    const params = this.buildIdentifyParameters(identifyLayerConfig, evt);
    if (params.layerIds.length === 0) {
      return from(Promise.resolve({ response: { results: [] }, identifyLayerConfig }));
    }
    return from(EsriIdentify.identify(identifyService.url, params)).pipe(
      map(response => ({ response, identifyLayerConfig }))
    );
  }

  private processIdentifyResponses(responses: FeatureIdentifyResult[], evt: __esri.ViewClickEvent) {
    this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
    let allResults: IdentifyResultInfo[] = [];
    // Combine results from all layers
    responses.forEach(response => {
      const resultsWithServiceInfo = this.mapResultWithConfig(response);
      allResults = allResults.concat(resultsWithServiceInfo);
    });

    allResults.forEach(res => this.extendedResultWithLayerInfo(res));

    // Process results
    if (allResults.length > 1) {
      this.popupService.buildIdentifyPopup({ results: allResults }, evt);
    } else if (allResults.length === 1) {
      const result: DetailViewFeatureParams = allResults[0].result as DetailViewFeatureParams;
      const layerName = result.editableLayers[0].fkLayer;
      this.mainMapService.handleNavigationByDetailView(layerName, result);
    }
  }

  private mapResultWithConfig(resp: FeatureIdentifyResult): IdentifyResultInfo[] {
    const { response, identifyLayerConfig } = resp;
    return response.results.map(result => ({
      result: result,
      identifyLayerConfig,
      popUpInfo: identifyLayerConfig.popUpsInfo.find(p => p.layerId === result.layerId),
    }));
  }

  private getIdentifyLayerConfig(url: string): IdentifyLayerConfig {
    const identifyConfig = this.configService.identifyConfig;
    const identifyLyr = this.layerContainerService.layerList.filter(f => f.fkLayer === url)[0];
    return identifyConfig.layers.filter(config => config.fkLayer === identifyLyr.fkLayer)[0];
  }

  private extendedResultWithLayerInfo(identifyResult: any) {
    identifyResult.result.workspace = identifyResult.identifyLayerConfig.workspace;
    identifyResult.result.fkIdentifyLayer = identifyResult.identifyLayerConfig.fkLayer;

    const isSelectedWorkspace =
      identifyResult.identifyLayerConfig.workspace === this.arbeitsbereichService.selectedArbeitsbereich.id;

    if (isSelectedWorkspace) {
      this.handleWorkspaceLayerInfo(identifyResult);
    } else {
      this.handleNonWorkspaceLayerInfo(identifyResult);
    }
    // add editable layer info
    this.addEditableLayerInfo(identifyResult);
  }

  private handleWorkspaceLayerInfo(identifyResult: any): void {
    const arbeitsBereichLyrs = this.layerContainerService.getActiveWorkspaceLyr(
      identifyResult.identifyLayerConfig.fkLayer
    );

    identifyResult.result.currentArbeitsBereichLyrs = arbeitsBereichLyrs;
    identifyResult.result.featureLayerUrl = `${arbeitsBereichLyrs[0].esriLayer['url']}/${identifyResult.result.layerId}`;
  }

  private handleNonWorkspaceLayerInfo(identifyResult: any): void {
    identifyResult.result.featureLayerUrl = `${this.layerContainerService.getIdentifyLayerUrl(
      identifyResult.identifyLayerConfig.fkLayer
    )}/${identifyResult.result.layerId}`;
  }

  private addEditableLayerInfo(identifyResult: any): void {
    const detailviewForms = this.configService.getDetailViewConfig(identifyResult.result.workspace);
    const form = detailviewForms?.forms?.find(f => f.fkIdentifyLayerId === identifyResult.result.layerId);
    const fkIdentifyLayerId = form?.editableLayerId;
    identifyResult.result.editableLayers = [{ fkLayer: fkIdentifyLayerId }];
  }

  private getIdentifyLayerIds(identifyLayerConfig: IdentifyLayerConfig): number[] {
    const identifyLyrIds: number[] = [];
    const identifyLyr: __esri.MapImageLayer = this.layerContainerService.getMapImageLayer(identifyLayerConfig.fkLayer);
    if (identifyLyr) {
      identifyLyr.sublayers.forEach((sublayer: __esri.Sublayer) => {
        if (identifyLayerConfig.layerIds.includes(sublayer.id) && sublayer.visible) {
          identifyLyrIds.push(sublayer.id);
        }
      });
    }
    return identifyLyrIds;
  }

  /**
   * @name sendResultToDetailView
   * @param result
   * @description sends the result to the detail view
   */
  public sendResultToDetailView(result: any) {
    this.selectionService.removeAllSelectedGraphics();
    this.selectionService.addSelectedGraphic(result.feature);
    this.widgetNotifyService.onShowFeatureSubject.next(result);
  }

  /**
   * @name activate
   * @description Sets to activate flag to true
   */
  public activate() {
    this.isActive = true;
  }

  /**
   * @name deactivate
   * @description Sets to activate flag to false
   */
  public deactivate() {
    this.isActive = false;
  }
}
